/* eslint-disable no-case-declarations, react/no-unknown-property, default-case */

import IconButton from '@material-ui/core/IconButton'
import { withStyles } from '@material-ui/core/styles'
import Close from '@material-ui/icons/Close'
import Delete from '@material-ui/icons/Delete'
import cn from 'classnames'
import { useEffect, useState } from 'react'

import Icon from '../Icon'
import { ExcelIcon, WordIcon } from '../ImagePreview/PreviewIcons'

function Preview({ file, fileType, className, src }) {
  const [FileViewerComponent, setFileViewerComponent] = useState(null)

  useEffect(() => {
    let isMounted = true

    if (typeof window === 'undefined') {
      return () => {
        isMounted = false
      }
    }

    import('react-file-viewer')
      .then(module => {
        if (!isMounted) return
        setFileViewerComponent(() => module.default || module)
      })
      .catch(() => {})

    return () => {
      isMounted = false
    }
  }, [])

  let fileName = ''
  if (src) {
    const regex = new RegExp(`%([^\\/]+)\\.${fileType}`)
    const match = src.match(regex)
    if (match) {
      fileName = `${match[1]}.${fileType}`
    }
  }

  switch (file ? file.type : fileType) {
    case 'image/jpeg':
    case 'image/png':
    case 'jpg':
    case 'jpeg':
    case 'png':
      return (
        <div className={className}>
          <a href={src} download fileType={fileType}>
            <img alt={file ? file.name : fileName} src={file ? URL.createObjectURL(file) : src} />
          </a>
          <div>{fileName}</div>
        </div>
      )
    case 'application/pdf':
    case 'pdf':
      return (
        <div className={className}>
          <a href={src} download fileType={fileType}>
            {FileViewerComponent ? (
              <FileViewerComponent filePath={file ? URL.createObjectURL(file) : src} fileType="pdf" />
            ) : null}
          </a>
          <div>{fileName}</div>
        </div>
      )
    case 'docx':
    case 'xlsx':
    case 'xls': {
      const previewSrc = getFilePreviewIcon(fileType)
      return (
        <div className={className}>
          <a href={src} download fileType={fileType}>
            <img alt={file ? file.name : fileName} src={previewSrc} />
          </a>
          <div>{fileName}</div>
        </div>
      )
    }
    default:
      return null
  }
}

function getFilePreviewIcon(fileType = '') {
  let filePath = ''
  switch (fileType.toLowerCase()) {
    case 'docx':
      filePath = WordIcon
      break
    case 'xlsx':
    case 'xls':
      filePath = ExcelIcon
      break
  }
  return filePath
}

const styles = theme => ({
  file: {
    position: 'relative',
    display: props => (props.src ? 'inline-flex' : 'flex'),
    flexDirection: 'column',
    padding: props => (props.src ? '8px' : '10px 16px'),
    border: theme.borders.default,
    borderRadius: theme.borders.borderRadius,
    fontSize: theme.typography.fontSizeSm,
    color: `var(--gameday-body-textColorAlt, ${theme.body.textColorAlt})`,
    minWidth: '38px',
    minHeight: '38px',
  },
  textRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: props => (props.src ? 0 : '18px'),
  },
  fileName: {
    display: 'flex',
    alignItems: 'center',
    height: '18px',
  },
  icon: {
    marginRight: '8px',
  },
  iconButton: {
    fontSize: theme.typography.fontSizeMed,
    padding: 0,
  },
  deleteButton: {
    position: props => props.src && 'absolute',
    top: '4px',
    right: '4px',
    backgroundColor: '#fff',
    padding: props => props.src && '4px',
    '&:hover': {
      backgroundColor: `var(--gameday-inputField-inputBGHover, ${theme.inputField.inputBGHover})`,
    },
  },
  preview: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    width: '100%',
    fontSize: '9px',
    padding: props => (!props.src ? '8px 0 16px' : 0),
    '& img': {
      display: 'block',
      maxWidth: '250px',
    },
    '& .pdf-viewer .pdf-canvas:not(:nth-child(2))': {
      display: 'none',
    },
  },
})

const File = ({
  children,
  classes,
  className,
  file,
  icon,
  onCancel,
  onDelete,
  previewFileType,
  src,
  success,
  uploading,
  variant,
  withPreview,
  disabled,
}) => {
  return (
    <div className={cn(classes.file, className)}>
      {withPreview && success && (
        <Preview className={classes.preview} file={file} src={src} fileType={previewFileType} />
      )}
      <div className={classes.textRow}>
        <div className={classes.fileName}>
          {icon && <Icon iconName={icon} iconSize={18} iconType={variant} className={classes.icon} />}
          {file ? file.name : ''}
        </div>
        {uploading && (
          <IconButton classes={{ root: classes.iconButton }} onClick={onCancel}>
            <Close fontSize="inherit" />
          </IconButton>
        )}
        {success && !disabled && (
          <IconButton className={classes.deleteButton} classes={{ root: classes.iconButton }} onClick={onDelete}>
            <Delete fontSize="inherit" />
          </IconButton>
        )}
      </div>
      {children}
    </div>
  )
}

const FileComponent = withStyles(styles)(File)
FileComponent.displayName = 'File'
export default FileComponent
